import { ModernLineItem } from "../__generated__/graphql";

/**
 * Returns up to two initials for a display name: the first letter of the
 * first word plus the first letter of the last word. Strips punctuation and
 * non-letter characters so names like "John . Smith" or "Smith Jr." don't
 * produce "J." or ".S".
 */
/**
 * Best-available display label for a user, in the order name → email → null.
 * Uses `||` semantics so empty strings also fall through, not just null/undefined.
 * Pair with `?? "Verse Medical"` at the call site when a string is required.
 */
export const userDisplayLabel = (
  name: string | null | undefined,
  email: string | null | undefined,
): string | null => {
  return name || email || null;
};

export const getInitials = (name: string | null | undefined): string => {
  if (!name) return "?";
  const letterWords = name
    .split(" ")
    .map((word) => word.replace(/[^\p{L}]/gu, ""))
    .filter((word) => word.length > 0);
  if (letterWords.length === 0) return "?";
  const first = letterWords[0][0];
  const last = letterWords[letterWords.length - 1][0];
  return (letterWords.length === 1 ? first : first + last).toUpperCase();
};

export const titleCase = (str: string) => {
  str = str.toLowerCase();
  str = str.replaceAll("_", " ");
  const strArr = str.split(" ");
  for (let i = 0; i < strArr.length; i++) {
    strArr[i] = strArr[i].charAt(0).toUpperCase() + strArr[i].slice(1);
  }
  return strArr.join(" ");
};

export const camelCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
};

export const camelCaseToStartCase = (text: string) => {
  // Step 1: Add space before each capital letter
  const spaced = text.replace(/([A-Z])/g, " $1");

  // Step 2: Capitalize the first letter of each word and remove extra spaces
  return spaced
    .replace(/^\s+|\s+$/g, "") // Trim any extra spaces
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// E.g. "medNecessity" -> "med Necessity", "AuthDenial" -> "Auth Denial"
const splitCamelCase = (text: string): string => {
  return text.replace(/([a-z])([A-Z])/g, "$1 $2");
};

// E.g. "medNecessity" -> "Med Necessity:"
export const camelCaseToLabel = (key: string): string => {
  const label = splitCamelCase(key);
  return label.charAt(0).toUpperCase() + label.slice(1) + ":";
};

// E.g. "medNecessity" -> "Enter med necessity"
export const camelCaseToPlaceholder = (key: string): string => {
  const label = splitCamelCase(key).toLowerCase();
  return `Enter ${label}`;
};

// E.g. "AuthDenial" -> "AUTH DENIAL"
export const camelCaseToUpperCase = (text: string): string => {
  return splitCamelCase(text).toUpperCase();
};

export const formatModernLineItemValuesForSummary = (
  lineItem: ModernLineItem,
) => {
  return Object.entries(lineItem)
    .filter(
      ([key, value]) =>
        key !== "categoryId" &&
        key !== "customProductName" &&
        key !== "formItemName" &&
        key !== "formItemOptions" &&
        key !== "__typename" &&
        key !== "id" &&
        key !== "formItemVariantId" &&
        key !== "hcpcsCode" &&
        // Surfaced through the `quantity` entry below, not as its own
        // "Quantity Is Maximum: true" line.
        key !== "quantityIsMaximum" &&
        // Keep the `quantity` key when "Maximum allowed amount" was selected
        // even though its numeric value is null, so it renders explicitly.
        (value != null ||
          (key === "quantity" && lineItem.quantityIsMaximum === true)),
    )
    .map(([key, value]) =>
      key === "quantity" && lineItem.quantityIsMaximum === true
        ? "Quantity: Maximum allowed amount"
        : `${camelCaseToStartCase(key)}: ${value}`,
    )
    .join("; ");
};

export const normalizePhoneNumber = (
  phoneNumber: string | null | undefined,
): string => {
  if (!phoneNumber) return "";
  // Remove all non-digits
  const cleaned = phoneNumber.replace(/\D/g, "");
  // Remove leading 1 if present
  return cleaned.startsWith("1") ? cleaned.slice(1) : cleaned;
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse a local `YYYY-MM-DD` string into a Date in local time (no UTC shift). */
export function isoToLocalDate(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** Returns the number of calendar days between today and the given date.
 *  Prefer passing the original date string rather than a pre-constructed Date
 *  object — `new Date("YYYY-MM-DD")` parses as UTC midnight, which loses the
 *  date-only format info needed to correct for western-timezone off-by-one. */
export const calendarDaysAgo = (date: Date | string): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Date-only strings (e.g. "2026-03-04") are parsed as UTC midnight by the
  // Date constructor.  Appending "T00:00:00" (no "Z") forces local-time
  // parsing so the calendar date isn't shifted in western timezones.
  const target =
    typeof date === "string" && DATE_ONLY_RE.test(date)
      ? new Date(date + "T00:00:00")
      : new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round(
    (today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24),
  );
};

/**
 * Formats a date value for use in HTML date input fields.
 * Handles Date objects, ISO strings, and other date formats.
 *
 * @param dateValue - The date value to format (Date, string, null, or undefined)
 * @returns A string in YYYY-MM-DD format for HTML date inputs, or empty string if invalid
 */
export const formatDateForInput = (
  dateValue: string | Date | null | undefined,
): string => {
  if (!dateValue) return "";

  try {
    let date: Date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === "string") {
      date = new Date(dateValue);
    } else {
      return "";
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "";
    }

    // Format as YYYY-MM-DD for HTML date input
    return date.toISOString().split("T")[0];
  } catch (error) {
    console.warn("Error formatting date for input:", error, dateValue);
    return "";
  }
};

/**
 * Formats a discharge date(time); `time` is null for date-only values.
 *
 * Discharge values are naive wall-clock strings (no timezone). We pin them to
 * UTC so the shown date/time and the past/future check are identical whether
 * rendered in a server PDF or the browser, instead of varying by runtime zone.
 */
export const getDischargeDisplay = (
  discharge: string | null | undefined,
): { isPast: boolean; date: string; time: string | null } | null => {
  if (!discharge) return null;
  const hasTime = discharge.includes("T");
  const base = hasTime ? discharge : `${discharge}T00:00:00`;
  // Only pin to UTC when the value carries no zone. Appending "Z" to a string
  // that already ends in "Z" or a numeric offset (e.g. ...T15:00:00.000Z) would
  // produce an invalid date, silently dropping the discharge text.
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(base);
  const d = new Date(hasZone ? base : `${base}Z`);
  if (isNaN(d.getTime())) return null;

  return {
    isPast: d.getTime() < Date.now(),
    date: d.toLocaleDateString("en-US", { timeZone: "UTC" }),
    time: hasTime
      ? d.toLocaleTimeString("en-US", {
          timeZone: "UTC",
          hour: "numeric",
          minute: "2-digit",
        })
      : null,
  };
};

export const getOrdinal = (num: number) => {
  const ordinal = [
    "First",
    "Second",
    "Third",
    "Fourth",
    "Fifth",
    "Sixth",
    "Seventh",
    "Eighth",
    "Ninth",
    "Tenth",
    "Eleventh",
    "Twelfth",
    "Thirteenth",
    "Fourteenth",
  ];

  if (num < 14) return ordinal[num];
  else return "";
};

const pascalToSentence = (pascalString: string): string => {
  const titleString: string = pascalString.replace(/([A-Z])/g, " $1");
  const words = titleString.trim().split(/\s+/);
  return words
    .map((word, i) =>
      i === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(" ");
};

const snakeToSentence = (snakeString: string) => {
  snakeString = snakeString.toLowerCase();
  snakeString = snakeString.replaceAll("_", " ");
  const splitString = snakeString.split(" ");
  for (let i = 0; i < splitString.length; i++) {
    splitString[i] =
      i === 0
        ? splitString[i].charAt(0).toUpperCase() + splitString[i].slice(1)
        : splitString[i];
  }
  return splitString.join(" ");
};

export const beautifyShippingStatus = (status: string) => {
  if (status.includes("_") || status.includes(" ")) {
    return snakeToSentence(status);
  } else {
    return pascalToSentence(status);
  }
};

/** A countdown as m:ss, e.g. 4:07 or 0:59. */
export const formatCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
};
